import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClockIcon } from "@phosphor-icons/react";
import { useGlobalAge } from "../context/AgeFilterContext";
import { useChain } from "../context/ChainContext";
import { fetchCoins } from "../lib/api";

// value: "all" or a positive number of hours (string).
export const ageMaxHours = (value) => {
  if (value === "all" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const filterByAge = (coins, value) => {
  const max = ageMaxHours(value);
  return max == null ? coins : coins.filter((c) => (c.ageHours ?? Infinity) < max);
};

export const ageLabel = (value) => {
  const max = ageMaxHours(value);
  if (max == null) return "all ages";
  if (max < 1) {
    const m = Math.round(max * 60);
    return `${m} minute${m === 1 ? "" : "s"}`;
  }
  const n = max % 1 === 0 ? max : Math.round(max * 10) / 10;
  return `${n} hour${n === 1 ? "" : "s"}`;
};

const PRESETS = [
  { id: "all", label: "All ages", tid: "all" },
  { id: "0.25", label: "15m", tid: "15m" },
  { id: "0.5", label: "30m", tid: "30m" },
  { id: "1", label: "1h", tid: "1h" },
  { id: "6", label: "6h", tid: "6h" },
  { id: "24", label: "24h", tid: "24h" },
];

// Master control shown once at the top of the Radar — drives every board.
export const GlobalAgeControl = () => {
  const { age, setAge } = useGlobalAge();
  const { chain } = useChain();
  const isPreset = PRESETS.some((p) => p.id === age);
  const [custom, setCustom] = useState(isPreset ? "" : age);
  useEffect(() => { setCustom(isPreset ? "" : age); }, [age, isPreset]);

  const { data: countData } = useQuery({
    queryKey: ["ageCounts", chain],
    queryFn: () => fetchCoins({ chain, limit: 250 }),
    refetchInterval: 60000,
    placeholderData: (p) => p,
    staleTime: 30000,
  });
  const universe = countData?.coins || [];
  const countFor = (id) => {
    if (id === "all") return universe.length;
    const h = Number(id);
    return universe.filter((c) => (c.ageHours ?? Infinity) < h).length;
  };
  const hasCounts = universe.length > 0;

  const commit = () => {
    const n = parseFloat(custom);
    if (Number.isFinite(n) && n > 0) setAge(String(n));
    else setCustom("");
  };

  return (
    <div data-testid="global-age-control" className="flex items-center gap-2.5 flex-wrap border border-neon/25 bg-neon/[0.05] px-4 py-3">
      <ClockIcon size={18} weight="bold" className="text-neon shrink-0" />
      <span className="text-sm font-semibold text-white">Show coins younger than</span>
      <div className="flex items-center gap-1.5 flex-wrap">
        {PRESETS.map((o) => {
          const active = age === o.id;
          const n = hasCounts ? countFor(o.id) : null;
          return (
            <button
              key={o.id}
              data-testid={`global-age-${o.tid}`}
              onClick={() => setAge(o.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border transition-colors ${
                active ? "bg-neon/15 border-neon text-neon" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
              }`}
            >
              {o.label}
              {n != null && <span className="font-mono text-[10px] opacity-70">({n})</span>}
            </button>
          );
        })}
        <div className={`flex items-center border transition-colors ${!isPreset ? "border-neon text-neon bg-neon/15" : "border-white/15 text-muted-foreground"}`}>
          <input
            data-testid="global-age-custom-input"
            type="number"
            min="0.1"
            step="0.5"
            inputMode="decimal"
            placeholder="custom"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
            onBlur={commit}
            className="w-[68px] bg-transparent px-2.5 py-1.5 text-xs font-bold outline-none placeholder:text-muted-foreground/60 placeholder:font-normal"
          />
          <span className="px-2 py-1.5 text-xs font-bold border-l border-white/15">h</span>
        </div>
      </div>
      <span className="text-xs text-muted-foreground hidden md:inline">— applies to every board below</span>
    </div>
  );
};
